import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Language, Translations } from './types';
import { en } from './translations/en';
import { ko } from './translations/ko';

const STORAGE_KEY = 'recovery_drill_language';

const dictionaries: Record<Language, Translations> = { en, ko };

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function detectLanguage(): Language {
  if (typeof window === 'undefined') return 'en';
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'ko') return saved;
    if (navigator.language && navigator.language.toLowerCase().startsWith('ko')) {
      return 'ko';
    }
  } catch {
    // Ignore localStorage errors
  }
  return 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLangState] = useState<Language>(detectLanguage);

  const setLanguage = (lang: Language) => {
    setLangState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    } catch {
      // Ignore localStorage errors
    }
  };

  useEffect(() => {
    try {
      document.documentElement.lang = language;
    } catch {
      // Ignore
    }
  }, [language]);

  const value: LanguageContextValue = {
    language,
    setLanguage,
    t: dictionaries[language] ?? en,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    return {
      language: 'en',
      setLanguage: () => {},
      t: en,
    };
  }
  return ctx;
}

export function useTranslation(): Translations {
  return useLanguage().t;
}
